import { useEffect, useState } from 'react';
import { CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import { Drawer } from '../../../components/ui/Drawer';
import remindersService from '../../../services/compliance/reminders.service';
import {
  AlertType,
  ReminderSchedule,
  ReminderScheduleCreate,
  ReminderScheduleUpdate,
} from '../../../types/compliance';

export interface ReminderScheduleDrawerProps {
  open: boolean;
  schedule: ReminderSchedule | null; // null = create mode
  onClose: () => void;
  onSaved: (s: ReminderSchedule) => void;
  onDeleted: () => void;
}

// Contract-scoped types are enabled. TPRM-scoped types are visible but
// disabled with a "(Phase 4)" suffix — they'll be wired up in Phase 4.
const ALERT_TYPE_OPTIONS: { value: AlertType; label: string; disabled?: boolean }[] = [
  { value: 'contract_renewal', label: 'Contract Renewal' },
  { value: 'contract_renewal_action', label: 'Contract Renewal Action' },
  { value: 'contract_notice_period', label: 'Contract Notice Period' },
  { value: 'tprm_review', label: 'TPRM Review (Phase 4)', disabled: true },
  { value: 'remediation_due', label: 'Remediation Due (Phase 4)', disabled: true },
  { value: 'risk_acceptance_expiry', label: 'Risk Acceptance Expiry (Phase 4)', disabled: true },
  { value: 'evidence_expiry', label: 'Evidence Expiry (Phase 4)', disabled: true },
];

// What each alert type counts back from, for generated labels.
const TRIGGER_WORDS: Record<AlertType, string> = {
  contract_renewal: 'renewal',
  contract_renewal_action: 'renewal action date',
  contract_notice_period: 'notice deadline',
  tprm_review: 'vendor review',
  remediation_due: 'remediation due date',
  risk_acceptance_expiry: 'risk acceptance expiry',
  evidence_expiry: 'evidence expiry',
};

/** Label used when the user leaves it blank, e.g. "30 days before renewal". */
export function autoReminderLabel(alertType: AlertType, daysBefore: number): string {
  if (!Number.isInteger(daysBefore) || daysBefore < 1) return '';
  return `${daysBefore} ${daysBefore === 1 ? 'day' : 'days'} before ${TRIGGER_WORDS[alertType]}`;
}

type FormState = {
  alert_type: AlertType;
  days_before: string; // kept as string for the input; coerced on submit
  label: string;
  is_active: boolean;
};

const emptyForm: FormState = {
  alert_type: 'contract_renewal',
  days_before: '30',
  label: '',
  is_active: true,
};

function scheduleToFormState(s: ReminderSchedule): FormState {
  return {
    alert_type: s.alert_type,
    days_before: String(s.days_before),
    // A label that is just the generated wording stays automatic, so editing
    // Days Before keeps it in step instead of leaving "30 days" on a 14-day rule.
    label: s.label === autoReminderLabel(s.alert_type, s.days_before) ? '' : s.label,
    is_active: s.is_active,
  };
}

export default function ReminderScheduleDrawer({
  open,
  schedule: initialSchedule,
  onClose,
  onSaved,
  onDeleted,
}: ReminderScheduleDrawerProps) {
  const [schedule, setSchedule] = useState<ReminderSchedule | null>(initialSchedule);
  const [form, setForm] = useState<FormState>(
    initialSchedule ? scheduleToFormState(initialSchedule) : emptyForm,
  );
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);

  // Re-seed local state whenever the drawer reopens (or the seed row changes).
  useEffect(() => {
    if (!open) return;
    setSchedule(initialSchedule);
    setForm(initialSchedule ? scheduleToFormState(initialSchedule) : emptyForm);
    setInlineError(null);
  }, [open, initialSchedule]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const generatedLabel = autoReminderLabel(form.alert_type, Number(form.days_before));
  const effectiveLabel = form.label.trim() || generatedLabel;

  const validate = (): string | null => {
    if (effectiveLabel.length > 100) return 'Label must be 100 characters or fewer.';
    const n = Number(form.days_before);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return 'Days Before must be an integer.';
    if (n < 1 || n > 3650) return 'Days Before must be between 1 and 3650.';
    if (!effectiveLabel) return 'Label is required.';
    return null;
  };

  const buildCreatePayload = (): ReminderScheduleCreate => ({
    alert_type: form.alert_type,
    days_before: Number(form.days_before),
    label: effectiveLabel,
    is_active: form.is_active,
  });

  const buildUpdatePayload = (): ReminderScheduleUpdate => buildCreatePayload();

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setInlineError(null);
    const err = validate();
    if (err) {
      setInlineError(err);
      return;
    }
    setSaving(true);
    try {
      if (schedule) {
        const updated = await remindersService.updateSchedule(schedule.id, buildUpdatePayload());
        setSchedule(updated);
        setForm(scheduleToFormState(updated));
        onSaved(updated);
      } else {
        const created = await remindersService.createSchedule(buildCreatePayload());
        setSchedule(created);
        setForm(scheduleToFormState(created));
        onSaved(created);
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      if (status === 409) {
        setInlineError('A schedule for this alert type + days already exists.');
      } else {
        setErrorNotification({ title: 'Failed to Save', message: detail });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!schedule) return;
    if (!window.confirm(`Are you sure you want to delete schedule "${schedule.label}"?`)) return;
    try {
      await remindersService.deleteSchedule(schedule.id);
      onDeleted();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Delete', message: msg });
    }
  };

  const isEdit = schedule !== null;
  const inputCls =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white';
  const labelCls = 'block text-sm font-medium text-label mb-1';

  const title = (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="truncate max-w-md">
        {isEdit && schedule ? schedule.label : 'New Schedule'}
      </span>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div>
        {isEdit && schedule && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <TrashIcon className="w-4 h-4" /> Delete
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleSubmit()}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="w-4 h-4" />
              {isEdit ? 'Save Changes' : 'Create Schedule'}
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Drawer
        isOpen={open}
        onClose={onClose}
        title={title}
        width="40vw"
        footer={footer}
        disableClickOutside
        contentClassName="relative flex-1 overflow-y-auto flex flex-col"
      >
        <div className="flex-1 px-4 sm:px-6 py-4 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="reminder-alert-type" className={labelCls}>
                  Alert Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="reminder-alert-type"
                  value={form.alert_type}
                  onChange={(e) => set('alert_type', e.target.value as AlertType)}
                  className={inputCls}
                  disabled={saving}
                >
                  {ALERT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="reminder-days-before" className={labelCls}>
                  Days Before <span className="text-red-500">*</span>
                </label>
                <input
                  id="reminder-days-before"
                  type="number"
                  min={1}
                  max={3650}
                  value={form.days_before}
                  onChange={(e) => set('days_before', e.target.value)}
                  className={inputCls}
                  disabled={saving}
                />
                <p className="text-xs text-gray-500 mt-1">
                  How many days before the trigger date the reminder should fire (1–3650).
                </p>
              </div>

              <div>
                <label htmlFor="reminder-label" className={labelCls}>
                  Label <span className="text-xs font-normal text-gray-500">(optional)</span>
                </label>
                <input
                  id="reminder-label"
                  type="text"
                  placeholder={generatedLabel}
                  value={form.label}
                  onChange={(e) => set('label', e.target.value)}
                  maxLength={100}
                  className={inputCls}
                  disabled={saving}
                />
              </div>

              <div>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => set('is_active', e.target.checked)}
                    disabled={saving}
                  />
                  Active
                </label>
              </div>

              {inlineError && (
                <div className="p-3 text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg">
                  {inlineError}
                </div>
              )}

              {/* Hidden submit so Enter within inputs triggers submit */}
              <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1}>
                <XMarkIcon className="w-0 h-0" />
              </button>
          </form>
        </div>
      </Drawer>

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </>
  );
}
